// src/components/membership/Membership.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { pageService } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheck, 
    faArrowRight, 
    faUserTie, 
    faUserGraduate, 
    faBuilding, 
    faBriefcase, 
    faUsers,
    faCrown,
    faStar
} from '@fortawesome/free-solid-svg-icons';

const Membership = () => {
    const [membershipTypes, setMembershipTypes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMembershipData();
    }, []);

    const fetchMembershipData = async () => {
        try {
            const response = await pageService.getMembership();
            setMembershipTypes(response.data.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching membership data:', error);
            // Updated fallback data
            setMembershipTypes([
                {
                    id: 1,
                    name: 'Professional Media Member',
                    icon: faUserTie,
                    description: 'For working journalists, reporters, editors, photographers, correspondents and other media professionals.',
                    fee: 2500,
                    benefits: [
                        'Access to all INMA events and workshops',
                        'Annual directory listing',
                        'Voting rights in elections',
                        'Professional networking opportunities',
                        'Legal assistance and support',
                        'Professional development programmes',
                        'Membership identity card and membership certificate are provided to the member'
                    ]
                },
                {
                    id: 2,
                    name: 'Student Media Member',
                    icon: faUserGraduate,
                    description: 'For journalism, mass communication and media students aspiring to build a career in the media industry.',
                    fee: 1000,
                    benefits: [
                        'Mentorship programmes',
                        'Internship opportunities',
                        'Access to INMA library and resources',
                        'Student discounts on events',
                        'Career guidance and counselling',
                        'Networking with industry professionals',
                        'Membership identity card and membership certificate are provided to the member'
                    ]
                },
                {
                    id: 3,
                    name: 'Corporate Member',
                    icon: faBuilding,
                    description: 'For companies, media houses, institutions and corporate organisations associated with or supporting the media sector.',
                    fee: 15000,
                    benefits: [
                        'Organization listing in INMA directory',
                        '10 individual memberships for employees',
                        'Priority sponsorship opportunities',
                        'Brand visibility in INMA events',
                        'Corporate networking events',
                        'Industry recognition and awards',
                        'Membership identity card and membership certificate are provided to the member'
                    ]
                },
                {
                    id: 4,
                    name: 'Business Member',
                    icon: faBriefcase,
                    description: 'For entrepreneurs, business professionals, publishers, advertisers and individuals engaged in commercial activities connected with media and communications.',
                    fee: 25000,
                    benefits: [
                        'Business networking opportunities',
                        'Advertising and promotional benefits',
                        'Access to media industry insights',
                        'Partnership opportunities',
                        'Business development support',
                        'Industry events and conferences',
                        'Membership identity card and membership certificate are provided to the member'
                    ]
                },
                {
                    id: 5,
                    name: 'General Member',
                    icon: faUsers,
                    description: 'For individuals who wish to associate with, support and participate in the objectives and activities of the Association.',
                    fee: 5000,
                    benefits: [
                        'Support the objectives of INMA',
                        'Participate in association activities',
                        'Access to general events',
                        'Community networking',
                        'Newsletter and updates',
                        'Volunteer opportunities',
                        'Membership identity card and membership certificate are provided to the member'
                    ]
                }
            ]);
            setLoading(false);
        }
    };

    const getIconColor = (index) => {
        const colors = ['#ffd700', '#00b894', '#0984e3', '#fd79a8', '#6c5ce7'];
        return colors[index % colors.length];
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <section className="py-5 bg-light">
            <Container>
                {/* Header Section */}
                <Row className="mb-5">
                    <Col className="text-center">
                        <h1 className="display-3 fw-bold text-primary">Membership Plans</h1>
                        <div className="divider mx-auto" style={{ width: '80px', height: '4px', background: 'linear-gradient(90deg, #0056b3, #007bff)', marginBottom: '1.5rem' }} />
                        <p className="lead text-muted mx-auto fw-medium" style={{ maxWidth: '700px', textAlign:'justify' }}>
                            The Indian Newspaper & Media Association welcomes individuals and organisations from different sectors of the media and public life under the following membership categories:
                        </p>
                    </Col>
                </Row>

                {/* Membership Cards */}
                <Row>
                    {membershipTypes.map((type, index) => (
                        <Col lg={4} md={6} key={type.id} className="mb-4">
                            <Card className="h-100 shadow-lg border-0 membership-card">
                                <Card.Header className="text-center border-0" style={{ 
                                    background: 'linear-gradient(135deg, #0056b3, #007bff)',
                                    padding: '1.5rem 1rem'
                                }}>
                                    <div className="mb-2">
                                        <FontAwesomeIcon 
                                            icon={type.icon} 
                                            size="3x" 
                                            style={{ color: getIconColor(index) }}
                                        />
                                    </div>
                                    <h4 className="fw-bold text-white mb-0">{type.name}</h4>
                                </Card.Header>
                                <Card.Body className="text-center p-4">
                                    <div className="price mb-2">₹{type.fee.toLocaleString()}</div>
                                    <Badge bg="primary" className="mb-3 px-3 py-2 rounded-pill">
                                        Membership
                                    </Badge>
                                    <p className="text-muted small">{type.description}</p>
                                    <hr />
                                    <ul className="list-unstyled text-start">
                                        {type.benefits.map((benefit, idx) => (
                                            <li key={idx} className="mb-2">
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="text-success me-2"
                                                />
                                                <span className="small">{benefit}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </Card.Body>
                                <Card.Footer className="text-center border-0 bg-white pb-4">
                                    <Link to="/register" className="text-decoration-none w-100">
                                        <Button 
                                            variant="primary" 
                                            className="w-100 rounded-pill py-2 fw-bold"
                                            style={{ 
                                                background: 'linear-gradient(135deg, #0056b3, #007bff)',
                                                border: 'none',
                                                fontSize: '1.1rem'
                                            }}
                                        >
                                            Apply Now{' '}
                                            <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                                        </Button>
                                    </Link>
                                </Card.Footer>
                            </Card>
                        </Col>
                    ))}
                </Row>

                
                {/* Need Help Section */}
                <Row className="mt-5">
                    <Col>
                        <Card className="shadow-lg border-0 p-4 text-center" style={{ 
                            background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                            color: 'white'
                        }}>
                            <Card.Body>
                                <h3 className="fw-bold mb-3">
                                    <FontAwesomeIcon icon={faUsers} className="me-2 text-warning" />
                                    Need Help Choosing?
                                </h3>
                                <p className="mb-3" style={{ fontSize: '1.1rem' }}>
                                    Contact our membership team for personalized assistance and guidance
                                </p>
                                <Link to="/contact">
                                    <Button variant="warning" className="rounded-pill px-4 py-2 fw-bold">
                                        Contact Us
                                    </Button>
                                </Link>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Membership Benefits Summary */}
                <Row className="mt-5">
                    <Col>
                        <Card className="shadow-lg border-0 p-4 bg-primary text-white">
                            <Card.Body className="text-center">
                                <h4 className="fw-bold mb-3">
                                    <FontAwesomeIcon icon={faCrown} className="me-2 text-warning" />
                                    Why Join INMA?
                                </h4>
                                <Row>
                                    <Col md={3} className="mb-2 mb-md-0">
                                        <p className="mb-0 fw-bold">Professional Growth</p>
                                        <small>Enhance your career prospects</small>
                                    </Col>
                                    <Col md={3} className="mb-2 mb-md-0">
                                        <p className="mb-0 fw-bold">Networking</p>
                                        <small>Connect with industry leaders</small>
                                    </Col>
                                    <Col md={3} className="mb-2 mb-md-0">
                                        <p className="mb-0 fw-bold">Recognition</p>
                                        <small>Get recognized for your work</small>
                                    </Col>
                                    <Col md={3}>
                                        <p className="mb-0 fw-bold">Support</p>
                                        <small>Legal and welfare assistance</small>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export default Membership;